import { useViewerStore } from '../../../../../../store/store';
import { parseRoofCombinationsFromAi } from '../ai-layout/parseRoofCombinationsFromAi';
import { canonicalCategoryName } from '../ai-layout/resolveRoofCombinationsForRunAi';
import { remapRoofFaceIdsToViewer } from './remapModulesLayoutRoofFaceIds';

type ResolveRoofIdsOpts = {
    defaultMaxfillRoofIds?: string[];
    bestRoofIds?: string[];
};

const CATEGORY_MAIN_KEY_PREFIX = '__category_main__:';

/** Resolve roof face IDs for a simulated layout key using current viewer store state. */
export function resolveRoofIdsForLayoutKey(key: string, opts?: ResolveRoofIdsOpts): string[] {
    const state = useViewerStore.getState() as any;

    if (key === 'default') {
        if (opts?.defaultMaxfillRoofIds?.length) {
            return opts.defaultMaxfillRoofIds;
        }
        if (opts?.bestRoofIds?.length) {
            return opts.bestRoofIds;
        }
        const sceneViewer = (state.sceneViewer ?? null) as { roofMeshArr?: Array<{ roofFaceId?: string }> } | null;
        const fromViewer = (sceneViewer?.roofMeshArr ?? [])
            .map((r) => r.roofFaceId)
            .filter((id): id is string => typeof id === 'string' && id.length > 0);
        if (fromViewer.length > 0) return fromViewer;
        return [];
    }
    const weights = state.aiModuleFillingScenarios?.[0]?.shadeWeights ?? [];
    const validIds = new Set<string>(
        (weights || [])
            .map((r: any) => r.roofFaceId)
            .filter((id: unknown): id is string => typeof id === 'string'),
    );
    const combos = parseRoofCombinationsFromAi(
        typeof state.aiResult === 'string' ? state.aiResult : '',
        validIds,
    );
    const resolved = (state.aiResolvedRoofCombinations as Array<{ name: string; roofFaceIds?: string[] }> | undefined) ?? [];
    const allCombos = resolved.length > 0 ? resolved : combos;

    const categoryLabel = key.startsWith(CATEGORY_MAIN_KEY_PREFIX)
        ? key.slice(CATEGORY_MAIN_KEY_PREFIX.length)
        : key;
    const lookupKeys = key.startsWith(CATEGORY_MAIN_KEY_PREFIX) ? [categoryLabel, key] : [key];

    for (const lookupKey of lookupKeys) {
        const targetCanonical = canonicalCategoryName(lookupKey);
        const combo = allCombos.find(
            (c) => c.name === lookupKey || canonicalCategoryName(c.name) === targetCanonical,
        );
        if (combo?.roofFaceIds?.length) {
            const viewer = (state.sceneViewer ?? null) as {
                roofMeshArr?: Array<{ roofFaceId?: string; userData?: Record<string, unknown> }>;
            } | null;
            return remapRoofFaceIdsToViewer(viewer, combo.roofFaceIds.filter(Boolean));
        }
    }

    const storedMain = (
        state.categoryMainLayoutByCategory as Record<string, { roofIds?: string[] }> | undefined
    )?.[categoryLabel];
    if (storedMain?.roofIds?.length) {
        const viewer = (state.sceneViewer ?? null) as {
            roofMeshArr?: Array<{ roofFaceId?: string; userData?: Record<string, unknown> }>;
        } | null;
        return remapRoofFaceIdsToViewer(viewer, storedMain.roofIds.filter(Boolean));
    }

    return [];
}
